import { Flex, Typography, Button, Card, Tag, message } from "antd";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { FileTextOutlined, UpOutlined } from "@ant-design/icons";
import { FaFilePdf } from "react-icons/fa6";
import { useEffect, useState } from "react";
import { downloadFileWithParams } from "../../../api/httpRequest";
import { DocData } from "../model/docData";
import { ContractSigningForm } from "./ContractSigningForm";
import { ContractRejectButton } from "./ContractRejectButton";
import { getContractConcludeDeadlineText, isContractSigningPeriodExpired } from "./contractSigningDeadline";
import { getContractConclusionStatus, isContractFullyConcluded } from "../config/contractStatus";

const { Text, Title } = Typography;

interface ContractCardProps {
    doc: DocData;
}

export const ContractCard = ({ doc }: ContractCardProps) => {
    const status = getContractConclusionStatus(doc);
    const displayStatus = doc.offer ? status : { text: "Заключен в бумажном виде", color: "green" };
    const { xl } = useBreakpoint();
    const isMobile = !xl;
    const [showSigningForm, setShowSigningForm] = useState(false);
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const [applicantSmsPending, setApplicantSmsPending] = useState(false);

    const signingExpired = isContractSigningPeriodExpired(doc);

    const canConclude = !isContractFullyConcluded(doc);

    useEffect(() => {
        setApplicantSmsPending(false);
    }, [doc.contractId]);

    const handleToggleSigning = () => {
        if (!canConclude) return;
        setShowSigningForm((prev) => !prev);
    };

    const deadlineHintText = getContractConcludeDeadlineText(doc);

    const handleDownload = () => {
        if (!doc.contractFileID || isDownloadLoading) {
            if (!doc.contractFileID) {
                message.warning("Файл договора недоступен");
            }
            return;
        }
        setIsDownloadLoading(true);
        downloadFileWithParams("/server/proxy/contracts/getFile", { FileID: doc.contractFileID })
            .then((response) => {
                const contentType = response.headers?.["content-type"] || "application/pdf";
                const blob = new Blob([response.data], { type: contentType });
                const href = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", `dogovor_${doc.contractId || "contract"}.pdf`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            })
            .catch(() => {
                message.error("Не удалось скачать файл");
            })
            .finally(() => {
                setIsDownloadLoading(false);
            });
    };

    return (
        <Card className="contract-card" hoverable>
            <Flex vertical gap={16}>
                {/* Десктоп — две колонки */}
                {!isMobile && (
                    <>
                        <Title level={5} style={{ margin: 0 }}>
                            {doc.specQualifier} {doc.specName}
                            {doc.TypeKG && <Text> ({doc.TypeKG})</Text>}
                        </Title>
                        <Flex gap={24}>
                            <Flex vertical gap={12} flex={1}>
                                {doc.klfName && (
                                    <Flex gap={8} wrap="wrap">
                                        <Text strong style={{whiteSpace: "nowrap"}}>Уровень образования:</Text>
                                        <Text>{doc.klfName}</Text>
                                    </Flex>
                                )}
                                {doc.profiles && (
                                    <Flex gap={8} wrap="wrap">
                                        <Text strong style={{whiteSpace: "nowrap"}}>Программы:</Text>
                                        <Text>{doc.profiles}</Text>
                                    </Flex>
                                )}
                                {doc.ktName && (
                                    <Flex gap={8} wrap="wrap">
                                        <Text strong style={{whiteSpace: "nowrap"}}>Форма обучения:</Text>
                                        <Text>{doc.ktName}</Text>
                                    </Flex>
                                )}
                            </Flex>
                            <Flex vertical gap={12} flex={1}>
                                <Flex gap={8} wrap="wrap" align="center">
                                    <Text strong style={{whiteSpace: "nowrap"}}>Статус:</Text>
                                    <Tag color={displayStatus.color}>{displayStatus.text}</Tag>
                                </Flex>
                                {doc.contract && (
                                    <Flex gap={8} wrap="wrap">
                                        <Text strong style={{whiteSpace: "nowrap"}}>Номер договора:</Text>
                                        <Text>{doc.contract}</Text>
                                    </Flex>
                                )}
                                {doc.price && (
                                    <Flex gap={8} wrap="wrap">
                                        <Text strong style={{whiteSpace: "nowrap"}}>Стоимость за семестр:</Text>
                                        <Text>{doc.price}</Text>
                                    </Flex>
                                )}
                            </Flex>
                        </Flex>
                    </>
                )}

                {/* Мобильный — строго вертикально */}
                {isMobile && (
                    <Flex vertical gap={12}>
                        <Title level={5} style={{ margin: 0 }}>
                            {doc.specQualifier} {doc.specName}
                            {doc.TypeKG && <Text> ({doc.TypeKG})</Text>}
                        </Title>
                        {doc.klfName && (
                            <Flex vertical gap={2}>
                                <Text strong>Уровень образования:</Text>
                                <Text>{doc.klfName}</Text>
                            </Flex>
                        )}
                        {doc.profiles && (
                            <Flex vertical gap={2}>
                                <Text strong>Программы:</Text>
                                <Text>{doc.profiles}</Text>
                            </Flex>
                        )}
                        {doc.ktName && (
                            <Flex vertical gap={2}>
                                <Text strong>Форма обучения:</Text>
                                <Text>{doc.ktName}</Text>
                            </Flex>
                        )}
                        <Flex vertical gap={2}>
                            <Text strong>Статус:</Text>
                            <Tag color={displayStatus.color}>{displayStatus.text}</Tag>
                        </Flex>
                        {doc.contract && (
                            <Flex vertical gap={2}>
                                <Text strong>Номер договора:</Text>
                                <Text>{doc.contract}</Text>
                            </Flex>
                        )}
                        {doc.price && (
                            <Flex vertical gap={2}>
                                <Text strong>Стоимость за семестр:</Text>
                                <Text>{doc.price}</Text>
                            </Flex>
                        )}
                    </Flex>
                )}

                {doc.offer && canConclude && deadlineHintText && (
                    <div className="contract-signing-form-hint contract-signing-form-hint--error">
                        {deadlineHintText}
                    </div>
                )}

                {doc.offer && (
                    <Flex gap={8} justify={isMobile ? "flex-start" : "flex-end"} wrap>
                        <Button
                            icon={<FaFilePdf />}
                            style={{ flex: isMobile ? 1 : "none" }}
                            disabled={!doc.contractFileID}
                            loading={isDownloadLoading}
                            onClick={handleDownload}
                        >
                            Скачать договор
                        </Button>
                        {canConclude && (
                            <ContractRejectButton
                                contractId={doc.contractId}
                                disabled={applicantSmsPending || signingExpired}
                                onSuccess={() => setShowSigningForm(false)}
                                flexStyle={isMobile ? 1 : "none"}
                            />
                        )}
                        <Button
                            type={showSigningForm ? "default" : "primary"}
                            disabled={!canConclude || signingExpired}
                            style={{ flex: isMobile ? 1 : "none" }}
                            icon={showSigningForm ? <UpOutlined /> : <FileTextOutlined />}
                            onClick={handleToggleSigning}
                        >
                            {showSigningForm ? "Скрыть форму подписания" : "Заключить договор"}
                        </Button>
                    </Flex>
                )}

                {doc.offer && canConclude && (
                    <div
                        className={`contract-signing-form-expand${showSigningForm ? " contract-signing-form-expand--open" : ""}`}
                        aria-hidden={!showSigningForm}
                    >
                        <div className="contract-signing-form-expand__inner">
                            <ContractSigningForm
                                doc={doc}
                                applicantSmsPending={applicantSmsPending}
                                onApplicantSmsPendingChange={setApplicantSmsPending}
                            />
                        </div>
                    </div>
                )}
            </Flex>
        </Card>
    );
};
